import React, { useRef } from "react";
import { TableRowData } from "./data-table";
import SidePanel, { PanelHeader } from "./side-panel";
import ScaledPreview from "./scaled-preview";
import { useIsMobile } from "../hooks/use-is-mobile";
import { ScrollArea } from "./ui/scroll-area";
import { Icons } from "./ui/icons";
import { MovementActivity } from "../lib/api";
import { format } from "date-fns";

const BLANK = "_______________________________";
const BLANK_ITEM_ROWS = 15;

interface WarehouseTransferDetailsDrawerProps {
  movementData: MovementActivity | undefined;
  warehouseTransferDetails: TableRowData | null;
  blank?: boolean;
  onClose: () => void;
}

const WarehouseTransferDetailsDrawer: React.FC<
  WarehouseTransferDetailsDrawerProps
> = ({ movementData, warehouseTransferDetails, blank = false, onClose }) => {
  const printRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();

  const items = movementData?.items ?? [];

  const fromWarehouseName = blank
    ? BLANK
    : movementData?.sourceWarehouse?.name ||
      movementData?.sourceSite?.name ||
      movementData?.sourceVendor?.fullName ||
      "N/A";

  const toWarehouseName = blank
    ? BLANK
    : movementData?.destinationWarehouse?.name ||
      movementData?.destinationSite?.name ||
      movementData?.destinationCustomer?.fullName ||
      "N/A";

  const transferNo = blank
    ? BLANK
    : movementData?.reference
      ? movementData.reference
      : "N/A";
  const displayDate = blank
    ? BLANK
    : movementData?.plannedDate
      ? format(new Date(movementData.plannedDate), "MMM dd, yyyy")
      : movementData?.createdAt
        ? format(new Date(movementData.createdAt), "MMM dd, yyyy")
        : "N/A";

  const driverName = blank
    ? BLANK
    : (movementData?.driverName ?? movementData?.driver_name ?? BLANK);
  const driverPhone = blank
    ? BLANK
    : (movementData?.driverPhoneNumber ??
      movementData?.driver_phone_number ??
      BLANK);
  const vehicleNo = blank
    ? BLANK
    : (movementData?.vehicleNumber ?? movementData?.vehicle_number ?? BLANK);

  const returnedBy = blank
    ? BLANK
    : (movementData?.returnedBy ?? movementData?.returned_by ?? BLANK);
  const returnedDate = blank
    ? BLANK
    : (movementData?.returnedDate ?? movementData?.returned_date)
      ? format(
          new Date(
            movementData.returnedDate ?? movementData?.returned_date ?? "",
          ),
          "MMM dd, yyyy",
        )
      : BLANK;
  const approvedBy = blank
    ? BLANK
    : (movementData?.approvedBy ?? movementData?.approved_by ?? BLANK);
  const receivedBy = blank
    ? BLANK
    : (movementData?.receivedBy ?? movementData?.received_by ?? BLANK);

  const handlePrint = () => {
    if (!printRef.current) return;
    const el = printRef.current;
    const parent = el.parentNode;
    const next = el.nextSibling;
    const originalTitle = document.title;

    document.body.appendChild(el);
    document.title = blank
      ? "Blank Warehouse Transfer Details"
      : `Warehouse Transfer Details ${fromWarehouseName} → ${toWarehouseName}`;
    window.print();

    document.title = originalTitle;
    if (parent) parent.insertBefore(el, next);
    else el.remove();
  };

  return (
    <SidePanel
      open={!!warehouseTransferDetails}
      onClose={onClose}
      width="w-[900px]"
      layer="overlay"
    >
      {warehouseTransferDetails && (
        <div className="relative h-full flex flex-col">
          <PanelHeader
            title={
              blank
                ? "Blank Warehouse Transfer Details"
                : `Transfer Details from ${fromWarehouseName} to ${toWarehouseName}`
            }
            onClose={onClose}
            actions={[
              {
                label: "Print",
                icon: Icons.Print,
                onClick: handlePrint,
                pinned: true,
              },
            ]}
          />

          <div className="flex-grow overflow-hidden">
            <ScrollArea className="h-full">
              <div
                className={
                  isMobile ? "w-full p-3 bg-gray-50" : "w-full p-6 bg-gray-50"
                }
              >
                <ScaledPreview>
                  <div
                    ref={printRef}
                    className="print-area p-4 bg-white shadow-lg rounded-lg"
                  >
                    <div className="text-center">
                      <h1 className="text-2xl font-bold text-gray-950 mb-2 border-b-2 border-gray-950 inline-block mx-auto">
                        WAREHOUSE TRANSFER DETAILS
                      </h1>
                    </div>
                    <p className="text-gray-950 text-right">No: {transferNo}</p>
                    <div className="text-center mb-4 border-b-2 border-gray-950 pb-2">
                      <img
                        src="/images/logo.png"
                        alt="Logo"
                        className="h-12 mx-auto mb-2"
                      />
                      <p className="text-sm text-gray-950">
                        Dealers in scaffolding & formwork (sale, rental and
                        fixing, cradle & scissor lift rental and sale)
                      </p>
                      <p className="text-sm text-gray-950">
                        Office: Plot 161, Block 161, Wazo, Tegeta
                      </p>
                      <p className="text-sm text-gray-950">
                        P.O Box 35854, Dar es Salaam, Tanzania. Sales:
                        +255(0)780 973 724 Warehouse: +255(0)780 973 728
                      </p>
                      <p className="text-sm text-gray-950">
                        Email: info@waposcaffolding.com Website:
                        www.waposcaffolding.co.tz
                      </p>
                    </div>

                    <div className="grid grid-cols-2 gap-6 mt-4 text-sm">
                      <div className="space-y-2">
                        <p className="text-gray-950">
                          <span className="font-semibold">Date:</span>{" "}
                          <span className="font-medium">{displayDate}</span>
                        </p>
                        <p className="text-gray-950">
                          <span className="font-semibold">
                            Transfer From Warehouse:
                          </span>{" "}
                          <span className="font-medium">
                            {fromWarehouseName}
                          </span>
                        </p>
                        <p className="text-gray-950">
                          <span className="font-semibold">
                            Transfer To Warehouse:
                          </span>{" "}
                          <span className="font-medium">{toWarehouseName}</span>
                        </p>
                      </div>

                      <div className="space-y-2">
                        <p className="text-gray-950">
                          <span className="font-semibold">Driver Name:</span>{" "}
                          <span className="font-medium">{driverName}</span>
                        </p>
                        <p className="text-gray-950">
                          <span className="font-semibold">
                            Driver Phone No:
                          </span>{" "}
                          <span className="font-medium">{driverPhone}</span>
                        </p>
                        <p className="text-gray-950">
                          <span className="font-semibold">Vehicle No:</span>{" "}
                          <span className="font-medium">{vehicleNo}</span>
                        </p>
                      </div>
                    </div>

                    <div className="mt-6">
                      <div className="text-center text-gray-950 font-semibold mb-2">
                        ITEMS BEING TRANSFERRED
                      </div>

                      <table className="w-full border border-gray-950 text-sm">
                        <thead>
                          <tr className="bg-gray-950">
                            <th className="text-white p-2 w-[70px] text-left">
                              No:
                            </th>
                            <th className="text-white p-2 text-left">Item</th>
                            <th className="text-white p-2 w-[90px] text-center">
                              Qty
                            </th>
                            <th className="text-white p-2 w-[90px] text-center">
                              Unit
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {blank ? (
                            Array.from({ length: BLANK_ITEM_ROWS }).map(
                              (_, index) => (
                                <tr key={index}>
                                  <td className="border border-gray-950 px-2 py-1 h-8">
                                    &nbsp;
                                  </td>
                                  <td className="border border-gray-950 px-2 py-1 h-8">
                                    &nbsp;
                                  </td>
                                  <td className="border border-gray-950 px-2 py-1 h-8">
                                    &nbsp;
                                  </td>
                                  <td className="border border-gray-950 px-2 py-1 h-8">
                                    &nbsp;
                                  </td>
                                </tr>
                              ),
                            )
                          ) : items.length > 0 ? (
                            items.map((item, idx) => (
                              <tr key={item.id || idx}>
                                <td className="border border-gray-950 px-2 py-1 text-gray-950">
                                  {String(idx + 1).padStart(2, "0")}.
                                </td>
                                <td className="border border-gray-950 px-2 py-1 text-gray-950">
                                  {[
                                    item.inventory?.description || "N/A",
                                    item.inventory?.length === "undefined" ||
                                    item.inventory?.length == null
                                      ? "---"
                                      : item.inventory.length,
                                  ]
                                    .filter(Boolean)
                                    .join(" • ")}
                                </td>
                                <td className="border border-gray-950 px-2 py-1 text-center text-gray-950">
                                  {item.totalQuantity ?? 0}
                                </td>
                                <td className="border border-gray-950 px-2 py-1 text-center text-gray-950">
                                  Pc.
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td
                                colSpan={4}
                                className="border border-gray-950 px-2 py-6 text-center text-gray-950"
                              >
                                No items found
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>

                    <div className="mt-10 grid grid-cols-3 gap-6 text-xs text-gray-950">
                      <div className="space-y-3">
                        <p className="font-semibold">SENDING WAREHOUSE:</p>
                        <p>Name: {returnedBy}</p>
                        <p>Signature: ________________________</p>
                        <p>Date: {returnedDate}</p>
                      </div>
                      <div className="space-y-3">
                        <p className="font-semibold">AUTHORIZED BY</p>
                        <p>Name: {approvedBy}</p>
                        <p>Signature: ________________________</p>
                        <p>Date: _____________________________</p>
                      </div>
                      <div className="space-y-3">
                        <p className="font-semibold">RECEIVING WAREHOUSE:</p>
                        <p>Name: {receivedBy}</p>
                        <p>Signature: ________________________</p>
                        <p>Date: _____________________________</p>
                      </div>
                    </div>
                  </div>
                </ScaledPreview>
              </div>
            </ScrollArea>
          </div>
        </div>
      )}
    </SidePanel>
  );
};

export default WarehouseTransferDetailsDrawer;
